import React, { FC, useEffect, useRef, useState } from "react";
import { Locale, LOCALES, useLocale } from "i18n/locale";

const LABELS: Record<Locale, { short: string; full: string }> = {
  en: { short: "EN", full: "English" },
  fr: { short: "FR", full: "Français" },
};

export interface LanguageSwitcherProps {
  className?: string;
}

const LanguageSwitcher: FC<LanguageSwitcherProps> = ({ className = "" }) => {
  const { locale, setLocale, t } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const choose = (next: Locale) => {
    setLocale(next);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t("common.language")}
        className="flex items-center gap-1 text-[12px] uppercase tracking-[1.2px] text-[#01152E] hover:opacity-70 transition-opacity"
        style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
      >
        {LABELS[locale].short}
        <svg
          className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <ul
          role="menu"
          className="absolute right-0 z-20 mt-2 min-w-[8rem] overflow-hidden rounded-xl border border-neutral-200 bg-white py-1 shadow-lg"
        >
          {LOCALES.map((option) => (
            <li key={option} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={option === locale}
                lang={option}
                onClick={() => choose(option)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm normal-case tracking-normal transition-colors hover:bg-cream-100 focus:bg-cream-100 focus:outline-none ${
                  option === locale ? "font-semibold text-[#01152E]" : "text-neutral-600"
                }`}
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                {LABELS[option].full}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
